import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { filterResponsive, rightResponsive } from '../../../common/cssclasses';
import { useTranslate } from '../../../components/TranslationText';
import HorseMarketFilter from '../Components/HorseMarketFilter';
import HorseMarketCard from '../Components/HorseMarketCard';
import { filterHorses, getHorseOwners } from '../Api/horseMarketApi';
import ActionButton from '../../../components/ActionButtons/NewActionButton';
import { checkIsReturning, consumeReturnTicket } from '../../../helper/pageFilterPersistence';

const TAKE = 10;

const initialFilters = {
    stallionName: null,
    priceFrom: null,
    priceTo: null,
    ownerId: null,
    skip: 0,
    take: TAKE,
};

const clearHorseMarketSessionStorage = () => {
    sessionStorage.removeItem("horseMarket_filters");
    sessionStorage.removeItem("horseMarket_return_from_details");
};

export default function HorseMarketListPage() {
    const { translate } = useTranslate();
    const navigationType = useNavigationType();
    const location = useLocation();
    const prevLocationKey = useRef(location.key);
    const isInitialMount = useRef(true);
    const wasReturningRef = useRef(false);

    if (isInitialMount.current) {
        isInitialMount.current = false;
        const isReturning = checkIsReturning("horseMarket_return_from_details", navigationType, "horseMarket");
        wasReturningRef.current = isReturning;
        if (!isReturning) {
            clearHorseMarketSessionStorage();
        }
    }

    const [filters, setFilters] = useState(() => {
        const saved = sessionStorage.getItem("horseMarket_filters");
        return saved ? JSON.parse(saved) : initialFilters;
    });
    const [horses, setHorses] = useState([]);
    const [buyers, setBuyers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const hasMore = horses.length < totalCount;

    const fetchHorsesList = useCallback(async (newFilters, append = false) => {
        const { sort, ...apiFilters } = newFilters;

        const res = await filterHorses(apiFilters);
        setTotalCount(res?.totalCount || 0);
        setHorses(prev =>
            append ? [...prev, ...(res?.result ?? [])] : res?.result ?? []
        );
    }, []);

    useEffect(() => {
        const init = async () => {
            const buyersRes = await getHorseOwners();
            setBuyers(buyersRes || []);

            if (wasReturningRef.current) {
                consumeReturnTicket("horseMarket_return_from_details");
                const saved = sessionStorage.getItem("horseMarket_filters");
                if (saved) {
                    const parsed = JSON.parse(saved);
                    fetchHorsesList(parsed);
                    return;
                }
            }

            const firstBuyer = buyersRes?.[0];
            const newFilters = {
                ...initialFilters,
                ownerId: firstBuyer?.id || null,
            };

            setFilters(newFilters);
            fetchHorsesList(newFilters);
        };

        init();
    }, [fetchHorsesList]);

    // Persist filters
    useEffect(() => {
        sessionStorage.setItem("horseMarket_filters", JSON.stringify(filters));
    }, [filters]);

    // Re-clicking menu resets to default
    useEffect(() => {
        if (prevLocationKey.current !== location.key) {
            prevLocationKey.current = location.key;
            if (navigationType === "PUSH") {
                clearHorseMarketSessionStorage();
                const firstBuyer = buyers?.[0];
                const defFilters = firstBuyer ? { ...initialFilters, ownerId: firstBuyer.id } : initialFilters;
                setFilters(defFilters);
                fetchHorsesList(defFilters);
            }
        }
    }, [location.key, navigationType, buyers, fetchHorsesList]);

    const handleSortChange = (e) => {
        const sortValue = e.target.value;
        const newFilters = {
            ...filters,
            sort: sortValue,
            skip: 0,
        };

        setFilters(newFilters);
        fetchHorsesList(newFilters);
    };

    const handleFilter = (filterArgs) => {
        const newFilters = {
            ...filters,
            ...filterArgs,
            skip: 0,
        };

        setFilters(newFilters);
        fetchHorsesList(newFilters);
    };

    const handleLoadMore = () => {
        const newFilters = {
            ...filters,
            skip: filters.skip + filters.take,
        };

        setFilters(newFilters);
        fetchHorsesList(newFilters, true);
    };

    const sortedHorses = [...horses];
    if (filters.sort === 'asc') {
        sortedHorses.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (filters.sort === 'desc') {
        sortedHorses.sort((a, b) => (b.price || 0) - (a.price || 0));
    } else if (filters.sort === 'date_asc') {
        sortedHorses.sort((a, b) => new Date(a.stallionBirthDate || a.birthDate || 0) - new Date(b.stallionBirthDate || b.birthDate || 0));
    } else if (filters.sort === 'date_desc') {
        sortedHorses.sort((a, b) => new Date(b.stallionBirthDate || b.birthDate || 0) - new Date(a.stallionBirthDate || a.birthDate || 0));
    } else if (filters.sort === 'name_asc') {
        sortedHorses.sort((a, b) => (a.stallionName || a.name || '').localeCompare(b.stallionName || b.name || ''));
    } else if (filters.sort === 'name_desc') {
        sortedHorses.sort((a, b) => (b.stallionName || b.name || '').localeCompare(a.stallionName || a.name || ''));
    }

    return (
        <div className="row rtl">
            <div className="col-12 d-flex justify-content-between align-items-center mb-main">
                <h2 className="fw-bold mb-0">
                    {translate('web.horseMarket', 'Horse Market')}
                </h2>
                <div className="d-flex justify-content-end">
                    <select
                        className="form-select"
                        style={{ width: '200px', cursor: 'pointer' }}
                        value={filters.sort || ''}
                        onChange={handleSortChange}
                    >
                        <option value="">{translate('web.sort', 'Sort')}</option>
                        <option value="asc">{translate('web.priceLowToHigh', 'Price: Low to High')}</option>
                        <option value="desc">{translate('web.priceHighToLow', 'Price: High to Low')}</option>
                        <option value="date_desc">{translate('web.dateNewestToOldest', 'Date: Newest to Oldest')}</option>
                        <option value="date_asc">{translate('web.dateOldestToNewest', 'Date: Oldest to Newest')}</option>
                        <option value="name_asc">{translate('web.nameAtoZ', 'Name: A to Z')}</option>
                        <option value="name_desc">{translate('web.nameZtoA', 'Name: Z to A')}</option>
                    </select>
                </div>
            </div>

            <div className={filterResponsive}>
                <HorseMarketFilter submit={handleFilter} />
            </div>

            <div className={rightResponsive}>
                <div className="row">
                    {sortedHorses.map(item => (
                        <HorseMarketCard key={item.id} horse={item} />
                    ))}
                </div>

                {hasMore && (
                    <div className="col-12 my-5 text-center">
                        <ActionButton
                            text={translate('web.loadMore', 'Load More')}
                            classNames="btn-danger w-unset"
                            isButton
                            onClick={handleLoadMore}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
